import {
    Alert,
    Anchor,
    Group,
    Stack,
    Text,
} from '@mantine/core';
import {NavLink} from 'react-router-dom';
import {
    PlatformPropertyCard,
    PlatformPropertyCardEntryInput,
} from '@flexinet/ui-components';

import {
    SyntheticUser,
    SyntheticUserState,
} from '../types';
import {
    LifecycleStageBadge,
    NextWakeCell,
    SyntheticUserStateBadge,
} from './Badges.tsx';

export const Profile = ({record}: { record: SyntheticUser }) => {

    const tombstoned = record.state === SyntheticUserState.TOMBSTONED;

    const identityProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Email',
            value: record.email,
            span: 12,
        }, {
            label: 'Synthetic User ID',
            value: record.id,
            span: 12,
        }, {
            label: 'Planned Lifespan',
            value: `${record.plannedLifespanDays} days`,
            span: 12,
        }, {
            label: 'Born',
            value: record.bornAt,
            formatter: 'timestamp',
            span: 12,
        }];

    // accountId / cognitoSub are the links to the real platform identity. On a
    // tombstone they are legitimately gone — labelled as such rather than blank.
    const linkageProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Account ID',
            value: record.accountId ?? (tombstoned ? 'account deleted' : 'n/a'),
            span: 12,
        }, {
            label: 'Cognito Sub',
            value: record.cognitoSub ?? (tombstoned ? 'user deleted' : 'n/a'),
            span: 12,
        }];
    // NOTE: secretArn is intentionally absent — it is never fetched, never shown,
    // and there is no affordance anywhere in this module that resolves it.

    const auditProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: 'Created By',
            value: record.createdByUsername ?? 'n/a',
            span: 12,
        }, {
            label: 'Created',
            value: record.created,
            formatter: 'timestamp',
            span: 12,
        }, {
            label: 'Last Updated',
            value: record.updated,
            formatter: 'timestamp',
            span: 12,
        }];

    return (
        <Stack gap={'xl'}>
            {/* A tombstone is a meaningful record, not an error — explain what it is
                rather than letting the page look broken (FNP-848). */}
            {tombstoned && (
                <Alert color={'gray'} title={'Tombstoned synthetic user'}>
                    The account behind this synthetic user has been deleted. This row is
                    retained on purpose as the record that the user existed — it is what
                    lets synthetic activity be told apart from organic activity. It will
                    not wake again.
                </Alert>
            )}

            <Stack gap={'xs'}>
                <Group justify={'space-between'}>
                    <Text size={'sm'} c={'dimmed'}>State</Text>
                    <SyntheticUserStateBadge state={record.state}/>
                </Group>
                <Group justify={'space-between'}>
                    <Text size={'sm'} c={'dimmed'}>Lifecycle Stage</Text>
                    <LifecycleStageBadge stage={record.lifecycleStage}/>
                </Group>
                <Group justify={'space-between'}>
                    <Text size={'sm'} c={'dimmed'}>Next Wake</Text>
                    <NextWakeCell record={record}/>
                </Group>
            </Stack>

            <PlatformPropertyCard properties={identityProperties}/>

            <Stack gap={'xs'}>
                <Text size={'sm'} c={'dimmed'}>Persona</Text>
                <Anchor
                    component={NavLink}
                    to={`/testing-personas/${encodeURIComponent(record.personaId)}`}
                    size={'sm'}
                >
                    {record.personaId}
                </Anchor>
            </Stack>

            <PlatformPropertyCard properties={linkageProperties}/>
            <PlatformPropertyCard properties={auditProperties}/>
        </Stack>
    );
};
